import { deviceAdminApi } from '@/api';
import { HelpFactoryComponent, Modal, WithLockedValidation } from '@/components/common';
import { handleResponse, unlockHandleBeforeUnload } from '@/Helper';
import { SecuredComponents, User } from '@/model/AccountModel';
import { BaseColumns, ResponseStatusCode } from '@/model/CommonModel';
import {
	CategoryType,
	CurrentDeviceControlObj,
	FunctionGroupConfig,
	FunctionGroupExtension,
	FunctionGroupExtensionData,
	FunctionGroupExtensionSaveModel,
} from '@/model/DeviceAdminModel';
import { HelpFeature } from '@/model/HelpModel';
import { Logger } from '@/model/LoggingModel';
import { useStoreSelector } from '@/store';
import { selectCurrentDevice } from '@/store/common/selectors';
import { Button, notification, Space } from 'antd';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { DefineExtensionView } from './DefineExtensionView/DefineExtensionView';
import { FunctionExtensionView } from './FunctionExtensionView/FunctionExtensionView';
import styles from './functionGroupModal.module.scss';

type Props = {
	functionGroupId: number;
	functionGroupName: string;
	onSetVisible: (visible: boolean, refresh?: boolean) => void;
};

export type FunctionGroupExtensionColumns = {
	Id: number;
	Name: string;
	Extension: string;
	Detail: string | React.ReactNode;
} & BaseColumns;

export type FunctionGroupExtensionFunctionsColumns = { FunctionName: string; DisplayName: string } & BaseColumns;

const user: User = getUser();
const componentPermission = User.getComponentPermission(user, SecuredComponents.Function_Group);
const topicIdNew: string = 'ToAddFunctionGroup';
const topicIdEdit: string = 'ToEditFunctionGroup';

const FunctionGroupModal: React.FC<Props> = WithLockedValidation(({ onSetVisible, functionGroupId, functionGroupName }) => {
	//#region state management variables
	const currentDevice: CurrentDeviceControlObj = useStoreSelector<CurrentDeviceControlObj>(selectCurrentDevice);
	const [functionGroupExtensionDataSource, setFunctionGroupExtensionDataSource] = useState<FunctionGroupExtensionColumns[]>([]);
	const [functionGroupExtension, setFunctionGroupExtension] = useState<FunctionGroupExtension[]>([]);
	const [funcGroupName, setFuncGroupName] = useState<string>('');
	const [funcGroupId, setFuncGroupId] = useState<number>();
	const [classNamesDefineFunction, setClassNamesDefineFunction] = useState<boolean>(false);
	const [functionGroupDataFunctions, setFunctionGroupDataFunctions] = useState<FunctionGroupExtensionData[]>([]);
	const [functionGroupConfigs, setFunctionGroupConfigs] = useState<FunctionGroupConfig>();
	const [extDigits, setExtDigits] = useState<string>();
	const [isDefineExtNew, setIsDefineExtNew] = useState<boolean>();
	const [deletedFunctionGroupExtensions, setDeletedFunctionGroupExtensions] = useState<FunctionGroupExtension[]>([]);
	//#endregion

	const isNew = functionGroupId === undefined;

	useEffect(() => {
		if (!isNew) {
			retrieveFunctionGroupExtensionsData();
		}
		retrieveFunctionGroupConfigurations();
		setFuncGroupName(functionGroupName);
		setFuncGroupId(functionGroupId);

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			handleBeforeUnload();
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, []);

	useEffect(() => {
		const mappedDataSource = createDataSource();
		setFunctionGroupExtensionDataSource(mappedDataSource);
	}, [JSON.stringify(functionGroupExtension)]);

	const handleBeforeUnload = () => {
		unlockHandleBeforeUnload(currentDevice.Id, currentDevice.DeviceObjectType);
	};

	const retrieveFunctionGroupConfigurations = async () => {
		try {
			const resConfig = await deviceAdminApi.getFunctionGroupConfigurations();
			if (resConfig.ResponseStatusCode === ResponseStatusCode.Success) {
				const result = resConfig.Entity;
				setFunctionGroupConfigs(result);
			}
		} catch (error) {
			Logger.writeErrorLog(error);
		}
	};

	const retrieveFunctionGroupExtensionsData = async () => {
		try {
			const res = await deviceAdminApi.getFunctionGroupExtensions(functionGroupId);
			if (!handleResponse(res)) {
				const result = res.Entity;
				setFunctionGroupExtension(result);
			}
		} catch (error) {
			Logger.writeErrorLog(error);
		}
	};

	const createDataSource = (): FunctionGroupExtensionColumns[] =>
		functionGroupExtension.map<FunctionGroupExtensionColumns>((item: FunctionGroupExtension) => {
			let isController: boolean = false;

			let detail: React.ReactNode[] = item.Data.map((itemDetail: FunctionGroupExtensionData) => {
				if (itemDetail.DoorGroupId !== -1 || itemDetail.MasterDoorGroupId !== -1) {
					return <span className={styles.details}>{itemDetail.DoorGroupName || itemDetail.MasterDoorGroupName}</span>;
				}

				isController = true;
				let detailInformation = '';
				const controllerName = itemDetail.ControllerName;
				const specialCategoryName = itemDetail.SpecialCategoryName;
				if (item.CategoryName == CategoryType.Relay || item.CategoryName == CategoryType.Alarm) {
					detailInformation = `${controllerName}\\${itemDetail.TimeZoneName}`;
				} else {
					if (specialCategoryName) {
						if (itemDetail.Function === 41) {
							detailInformation = `${controllerName}\\${itemDetail.FunctionName}: ${specialCategoryName}`;
						} else {
							detailInformation = `${controllerName}\\${specialCategoryName}`;
						}
					} else {
						detailInformation = controllerName;
					}
				}

				return (
					<span
						className={cx(styles.details, {
							[styles.detailItem]: item.Data.length > 1,
						})}>
						{detailInformation}
					</span>
				);
			});

			const functionNames: string[] = Array.from(
				new Set<string>(
					item.Data.map((dataItem: FunctionGroupExtensionData) => {
						return dataItem.FunctionName;
					})
				)
			);
			let functionName = _('NoFunctionDefined');
			const showFuncNamesAndDetails = functionNames.length >= 2 || (!isController && detail.length > 1);
			if (showFuncNamesAndDetails) {
				functionName = _('MultiFunction');
			} else if (functionNames.length > 0 && functionNames[0] !== '') {
				functionName = functionNames[0];
			}

			return {
				Id: item.ExtensionId,
				key: `${funcGroupId?.toString() ?? 0}-${item.Extension}`,
				Name: functionName,
				Extension: item.Extension === '' ? '<>' : item.Extension,
				Detail: showFuncNamesAndDetails ? '' : detail,
			};
		});

	const parseKey = (key: string): number => {
		let [_, idNumber] = key.split('-');
		if (idNumber === '') {
			idNumber = '-1';
		}
		return parseInt(idNumber);
	};

	const setRowKey = (key: string) => {
		defineExtensionChange(parseKey(key));
	};

	const setFnGroupName = (newFnGroupName: string) => {
		if (newFnGroupName.length < funcGroupName.length) {
			setFuncGroupName(newFnGroupName);
		} else if (!newFnGroupName.includes('%')) {
			setFuncGroupName(newFnGroupName);
		}
	};

	const defineExtensionChange = (selectedKey?: number) => {
		const foundIndex = functionGroupExtension.findIndex(item => parseInt(item.Extension) === selectedKey);
		if (selectedKey >= 0) {
			setExtDigits(selectedKey.toString());
			setFunctionGroupDataFunctions(functionGroupExtension[foundIndex]?.Data);
			setIsDefineExtNew(false);
		} else if (selectedKey === -1) {
			setExtDigits('');
			const emptyExtension = functionGroupExtension.find(x => x.Extension === '');
			if (emptyExtension) {
				setFunctionGroupDataFunctions(emptyExtension?.Data);
			}
			setIsDefineExtNew(false);
		} else {
			setExtDigits(undefined);
			setFunctionGroupDataFunctions([]);
			setIsDefineExtNew(true);
		}
		setClassNamesDefineFunction(!classNamesDefineFunction);
	};

	const addCodeExtension = (addingData: FunctionGroupExtensionData[], extId: string, fnCat?: string) => {
		const extension = extId?.toString() ?? '';
		const foundFuncGroupExt = functionGroupExtension.find(fGrpExt => fGrpExt.Extension === extension);
		if (foundFuncGroupExt && extDigits === undefined) {
			notification['warning']({
				message: _('ThisExtensionAlreadyDefined'),
			});
		} else if (!foundFuncGroupExt) {
			functionGroupExtension.push({
				ExtensionId: -1,
				CategoryName: fnCat || '',
				CustomizedCategoryName: '',
				CustomizedFunctionName: '',
				Data: addingData,
				Extension: extId?.toString() ?? '',
				IsDeleted: false,
			});
			setFunctionGroupExtension(functionGroupExtension);
		} else {
			foundFuncGroupExt.CategoryName = fnCat || foundFuncGroupExt.CategoryName;
			foundFuncGroupExt.Data = addingData;
			setFunctionGroupExtension(functionGroupExtension);
		}
	};

	const deleteCodeExtensions = (keys: string[]): Promise<boolean> => {
		const clonedData = structuredClone(functionGroupExtension);
		const parsedKeys: string[] = keys.map(key => key.split('-').at(-1));
		parsedKeys.forEach(parsedKey => {
			const foundExt = clonedData.find(item => item.Extension === parsedKey);
			if (foundExt) {
				foundExt.IsDeleted = true;
			}
		});
		const filteredExtensions = clonedData.filter(fge => !fge.IsDeleted);
		const deletedExtensions = clonedData.filter(fge => fge.IsDeleted);
		setDeletedFunctionGroupExtensions([...deletedFunctionGroupExtensions, ...deletedExtensions]);
		setFunctionGroupExtension(filteredExtensions);
		return Promise.resolve(true);
	};

	const modalTitle = isNew ? _('AddFunctionGroup') : _('EditFunctionGroup');
	const handleOnCloseModal = () => onSetVisible(false);

	const handleOnSave = () => {
		const name: string = funcGroupName.trim();
		const saveModel: FunctionGroupExtensionSaveModel = {
			HasNameChanged: name.toLowerCase() !== functionGroupName.toLowerCase(),
			FunctionGroup: {
				FunctionGroupId: funcGroupId,
				Name: name,
				Defined: true,
				ControllerIds: [],
			},
			FunctionGroupExtensions: [...functionGroupExtension, ...deletedFunctionGroupExtensions],
		};

		const api = funcGroupId > 0 ? deviceAdminApi.saveFunctionGroup : deviceAdminApi.addNewFunctionGroup;
		api(saveModel).then(response => {
			if (!handleResponse(response)) {
				onSetVisible(false, true);
			}
		});
	};

	const helpTopicKey: string = isNew ? topicIdNew : topicIdEdit;

	return (
		<Modal
			footer={
				classNamesDefineFunction
					? undefined
					: [
							<div className={styles.buttonsContainer}>
								<Space>
									<Button
										disabled={!componentPermission.canUpdate}
										id='functionGroupModalSaveButton'
										key='save'
										type='primary'
										onClick={handleOnSave}>
										{_('SaveChanges')}
									</Button>
									<Button id='functionGroupCancelButton' key='cancel' onClick={handleOnCloseModal}>
										{_('Cancel')}
									</Button>
								</Space>
							</div>,
					  ]
			}
			width='900px'
			onCancel={handleOnCloseModal}
			title={classNamesDefineFunction ? '' : modalTitle}
			className={classNamesDefineFunction ? styles.extendedModal : styles.modal}
			visible={true}
			hideCloseButton={classNamesDefineFunction}
			addHelpButton={<HelpFactoryComponent helpFeature={HelpFeature.FunctionGroups} topicId={helpTopicKey} size='medium' />}>
			{classNamesDefineFunction ? (
				<DefineExtensionView
					defineExtensionChange={defineExtensionChange}
					addCodeExtension={addCodeExtension}
					functionGroupExtensionId={extDigits}
					configurations={functionGroupConfigs}
					functionGroupExtensionData={functionGroupDataFunctions}
					isNew={isDefineExtNew}
					helpTopicKey={helpTopicKey}
				/>
			) : (
				<FunctionExtensionView
					setRowKey={setRowKey}
					defineExtensionChange={defineExtensionChange}
					setFnGroupName={setFnGroupName}
					functionGroupExtensionData={functionGroupExtensionDataSource}
					functionGroupName={funcGroupName}
					deleteCodeExtensions={deleteCodeExtensions}
				/>
			)}
		</Modal>
	);
});

export { FunctionGroupModal };
